import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Trophy, Medal, Award, Globe, Github, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeaderboardPage() {
  const { t } = useLanguage();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard?limit=50`);
      setLeaderboard(Array.isArray(res.data?.leaderboard) ? res.data.leaderboard : []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-slate-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-400" />;
    return <span className="text-xl font-bold text-slate-500">#{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-slate-400/20 to-slate-500/10 border-slate-400/30';
    if (rank === 3) return 'bg-gradient-to-r from-orange-500/20 to-orange-600/10 border-orange-500/30';
    return 'bg-white/5 border-white/10';
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      development: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      live: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return styles[status] || 'bg-slate-500/20 text-slate-400';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 80) return 'text-emerald-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-orange-400';
  };

  return (
    <div className="min-h-screen bg-[#000000]" data-testid="leaderboard-page">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Trophy className="w-10 h-10 text-yellow-400" />
              <h1 className="text-3xl md:text-4xl font-bold text-white" data-testid="leaderboard-headline">
                Builder Leaderboard
              </h1>
            </div>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Top builders in the We·R2 Chain ecosystem ranked by their proposal quality and team credentials
            </p>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="glass-card rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-white">{leaderboard.length}</div>
              <div className="text-sm text-slate-400">Total Builders</div>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-green-400">
                {leaderboard.filter(b => b.status === 'live').length}
              </div>
              <div className="text-sm text-slate-400">Live Projects</div>
            </div>
            <div className="glass-card rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-yellow-400">
                {leaderboard.filter(b => b.status === 'development').length}
              </div>
              <div className="text-sm text-slate-400">In Development</div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Award className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No approved builders yet. Be the first to join the leaderboard!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((builder) => (
                <div 
                  key={`${builder.team_name}-${builder.rank}`}
                  className={`glass-card rounded-xl p-4 md:p-6 border ${getRankBg(builder.rank)} transition-all hover:scale-[1.01]`}
                  data-testid={`leaderboard-rank-${builder.rank}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className="w-12 h-12 flex items-center justify-center">
                      {getRankIcon(builder.rank)}
                    </div>

                    {/* Main Info */}
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-lg font-semibold text-white">{builder.team_name}</h3>
                        <Badge className={getStatusBadge(builder.status)}>
                          {builder.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {builder.country}
                        </span>
                        <span className="text-slate-300">{builder.project}</span>
                      </div>
                    </div>

                    {/* Links */}
                    <div className="hidden md:flex items-center gap-3">
                      {builder.github && (
                        <a 
                          href={builder.github} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                        >
                          <Github className="w-5 h-5" />
                        </a>
                      )}
                      {builder.website && (
                        <a 
                          href={builder.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      )}
                    </div>

                    {/* Score */}
                    <div className="text-right">
                      <div className={`text-2xl md:text-3xl font-bold ${getScoreColor(builder.score)}`}>
                        {builder.score}
                      </div>
                      <div className="text-xs text-slate-500">points</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
